import React from "react";
import PropTypes from "prop-types";
import { TimerDisplay } from "../TimerDisplay";
import { ButtonControls } from "../ButtonControls";
import { StartStopButton } from "../StartStopButton";
import { MCStateContext } from "../../../..";

import "./style.scss";
import { useTranslation } from "react-i18next";

const HOUR_RATE = 1;
const MINUTE_RATE = 5;
const LOWERBOUND_TIME = new Date(0, 0, 0).getTime();
const TRANSLATION_KEY = {
  TIMER: "Timer.string",
  HH: "HH.string",
  MM: "MM.string",
  SS: "SS.string",
  INCREASE_HOUR_BUTTON: "IncreaseHourButton.string",
  DECREASE_HOUR_BUTTON: "DecreaseHourButton.string",
  INCREASE_MINUTES_BUTTON: "IncreaseMinutesButton.string",
  DECREASE_MINUTES_BUTTON: "DecreaseMinutesButton.string",
};

export function TimerBlock(props) {
  const { pulsing, running, balanced, isLidOpen } =
    React.useContext(MCStateContext);

  const incrementHours = () => {
    const time = new Date(props.value.getTime());
    time.setHours(time.getHours() + HOUR_RATE);
    props.setTimer(time);
  };
  const decrementHours = () => {
    if (props.value.getTime() === LOWERBOUND_TIME)
      props.setTimer(new Date(LOWERBOUND_TIME));
    else {
      const time = new Date(props.value.getTime());
      time.setHours(time.getHours() - HOUR_RATE);
      props.setTimer(time);
    }
  };

  const incrementMinutes = () => {
    const time = new Date(props.value.getTime());
    time.setMinutes(time.getMinutes() + MINUTE_RATE);
    props.setTimer(time);
  };
  const decrementMinutes = () => {
    if (props.value.getTime() === LOWERBOUND_TIME)
      props.setTimer(new Date(LOWERBOUND_TIME));
    else {
      const time = new Date(props.value.getTime());
      time.setMinutes(time.getMinutes() - MINUTE_RATE);
      props.setTimer(time);
    }
  };

  React.useEffect(() => {
    if (running) {
      props.setTimer(LOWERBOUND_TIME);
    }
  }, [running]);

  const startStopActive =
    !pulsing &&
    balanced &&
    !isLidOpen &&
    props.value.getTime() != LOWERBOUND_TIME;

  const onClickStart = () => {
    startStopActive && props.onRunCentrifuge();
  };

  const onClickStop = () => {
    !pulsing && props.onStopCentrifuge();
  };

  const { t } = useTranslation();

  const time = new Date(props.value.getTime());
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();

  return (
    <div className="mc_settings_timer">
      <h5 className="mc_settings_timer_title">{t(TRANSLATION_KEY.TIMER)}</h5>
      <TimerDisplay timeData={props.value} setTimer={props.setTimer} />
      <div className="mc_settings_timer_controlBlock">
        <ButtonControls
          text={t(TRANSLATION_KEY.HH)}
          increment={incrementHours}
          decrement={decrementHours}
          altText1={t(TRANSLATION_KEY.INCREASE_HOUR_BUTTON, {
            hours: hours,
            minutes: minutes,
            seconds: seconds,
          })}
          altText2={t(TRANSLATION_KEY.DECREASE_HOUR_BUTTON, {
            hours: hours,
            minutes: minutes,
            seconds: seconds,
          })}
        />
        <ButtonControls
          text={t(TRANSLATION_KEY.MM)}
          increment={incrementMinutes}
          decrement={decrementMinutes}
          altText1={t(TRANSLATION_KEY.INCREASE_MINUTES_BUTTON, {
            hours: hours,
            minutes: minutes,
            seconds: seconds,
          })}
          altText2={t(TRANSLATION_KEY.DECREASE_MINUTES_BUTTON, {
            hours: hours,
            minutes: minutes,
            seconds: seconds,
          })}
        />
      </div>
      <StartStopButton
        onClickStart={onClickStart}
        onClickStop={onClickStop}
        disabled={!startStopActive}
      />
    </div>
  );
}

TimerBlock.propTypes = {
  /**
   * timer value to be displayed
   */
  value: PropTypes.instanceOf(Date),
  /**
   * callback function to set timer value
   */
  setTimer: PropTypes.func,
  /**
   * callback function to start centrifuge
   */
  onRunCentrifuge: PropTypes.func,
  /**
   * callback function stop centrifuge
   */
  onStopCentrifuge: PropTypes.func,
};

TimerBlock.defaultProps = {
  timeInterval: 500,
};
